import { Pagination } from "services/search/types";
import { DataWithPagination } from "services/api/types";
import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "../transformation";
import {
  NotificationDefinition,
  NotificationDefinitionRow,
  NotificationDefinitionFormValues,
  NotificationLog,
  NotificationInboxResponse,
  NotificationCalendarDay,
  EntityTypeOption,
  EntityItem,
  NotificationSearchFormValues,
  NotificationLogSearchFormValues,
  VehicleTimelineGroup,
} from "./types";
import {
  transformNotificationDefinition,
  transformNotificationDefinitionRow,
  transformNotificationDefinitionOnCreate,
  transformNotificationLog,
  transformEntityItem,
} from "./transformations";

/*---------------------------------------------------------------------------------------
    DEFINITIONS — SEARCH
-----------------------------------------------------------------------------------------*/
export const getNotificationDefinitionsAPI = async (
  pagination: Pagination,
  filters?: NotificationSearchFormValues | null
): Promise<DataWithPagination<NotificationDefinitionRow>> => {
  let path = `/v1/notifications?`;

  if (filters?.entityType) path += `&entityType=${filters.entityType}`;
  if (filters?.isActive) path += `&isActive=${filters.isActive}`;
  if (filters?.scope) path += `&scope=${filters.scope}`;
  if (filters?.channel) path += `&channel=${filters.channel}`;
  if (filters?.isCustom) path += `&isCustom=${filters.isCustom}`;
  if (filters?.search) path += `&search=${encodeURIComponent(filters.search)}`;

  return await paginatedApiCall(path, pagination, transformNotificationDefinitionRow);
};

/*---------------------------------------------------------------------------------------
    DEFINITIONS — GET BY ID
-----------------------------------------------------------------------------------------*/
export const getNotificationDefinitionAPI = async (
  id: string
): Promise<NotificationDefinition> => {
  const response = await createApiCall(
    {
      path: `/v1/notifications/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformNotificationDefinition(response.data);
};

/*---------------------------------------------------------------------------------------
    DEFINITIONS — CREATE
-----------------------------------------------------------------------------------------*/
export const createNotificationDefinitionAPI = async (
  formValues: NotificationDefinitionFormValues,
  t: (key: string) => string
): Promise<NotificationDefinition> => {
  const response = await createApiCall(
    {
      path: `/v1/notifications`,
      method: "POST",
      body: transformNotificationDefinitionOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => t("admin.notification.create.success"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformNotificationDefinition(response.data);
};

/*---------------------------------------------------------------------------------------
    DEFINITIONS — UPDATE
-----------------------------------------------------------------------------------------*/
export const updateNotificationDefinitionAPI = async (
  id: string,
  formValues: NotificationDefinitionFormValues,
  t: (key: string) => string
): Promise<NotificationDefinition> => {
  const response = await createApiCall(
    {
      path: `/v1/notifications/${id}`,
      method: "PUT",
      body: transformNotificationDefinitionOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => t("admin.notification.update.success"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformNotificationDefinition(response.data);
};

/*---------------------------------------------------------------------------------------
    DEFINITIONS — DELETE
-----------------------------------------------------------------------------------------*/
export const deleteNotificationDefinitionAPI = async (
  id: string,
  t: (key: string) => string
): Promise<void> => {
  await createApiCall(
    {
      path: `/v1/notifications/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.notification.delete.success"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();
};

/*---------------------------------------------------------------------------------------
    DEFINITIONS — TOGGLE ACTIVE
-----------------------------------------------------------------------------------------*/
export const toggleNotificationDefinitionActiveAPI = async (
  id: string
): Promise<NotificationDefinition> => {
  const response = await createApiCall(
    {
      path: `/v1/notifications/${id}/toggle-active`,
      method: "PATCH",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformNotificationDefinition(response.data);
};

/*---------------------------------------------------------------------------------------
    LOGS
-----------------------------------------------------------------------------------------*/
export const getNotificationLogsAPI = async (
  pagination: Pagination,
  filters?: NotificationLogSearchFormValues | null
): Promise<DataWithPagination<NotificationLog>> => {
  let path = `/v1/notifications/logs?`;

  if (filters?.vehicleId) path += `&vehicleId=${filters.vehicleId}`;
  if (filters?.channel) path += `&channel=${filters.channel}`;
  if (filters?.isSuccess) path += `&isSuccess=${filters.isSuccess}`;

  return await paginatedApiCall(path, pagination, transformNotificationLog);
};

/*---------------------------------------------------------------------------------------
    CALENDAR
-----------------------------------------------------------------------------------------*/
export const getNotificationCalendarAPI = async (
  from: string,
  to: string
): Promise<NotificationCalendarDay[]> => {
  const response = await createApiCall(
    {
      path: `/v1/notifications/calendar?from=${from}&to=${to}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data;
};

export const getVehicleTimelineAPI = async (
  from: string,
  to: string
): Promise<VehicleTimelineGroup[]> => {
  const response = await createApiCall(
    {
      path: `/v1/notifications/calendar/vehicles?from=${from}&to=${to}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data;
};

/*---------------------------------------------------------------------------------------
    INBOX
-----------------------------------------------------------------------------------------*/
export const getNotificationInboxAPI = async (): Promise<NotificationInboxResponse> => {
  const response = await createApiCall(
    {
      path: `/v1/notifications/inbox`,
      method: "GET",
    },
    undefined
  )();

  return response.data;
};

export const markNotificationReadAPI = async (id: string): Promise<void> => {
  await createApiCall(
    {
      path: `/v1/notifications/inbox/${id}/read`,
      method: "PUT",
    },
    undefined
  )();
};

export const markAllNotificationsReadAPI = async (): Promise<void> => {
  await createApiCall(
    {
      path: `/v1/notifications/inbox/read-all`,
      method: "PUT",
    },
    undefined
  )();
};

/*---------------------------------------------------------------------------------------
    ENTITY TYPES / ITEMS
-----------------------------------------------------------------------------------------*/
export const getEntityTypesAPI = async (): Promise<EntityTypeOption[]> => {
  const response = await createApiCall(
    {
      path: `/v1/notifications/entity-types`,
      method: "GET",
    },
    undefined
  )();

  return response.data;
};

export const getEntityItemsAPI = async (
  entityType: number
): Promise<EntityItem[]> => {
  const response = await createApiCall(
    {
      path: `/v1/notifications/entity-types/${entityType}/items`,
      method: "GET",
    },
    undefined
  )();

  return response.data;
};

export const getEntityItemsPagedAPI = async (
  entityType: number,
  pagination: Pagination,
  search?: string
): Promise<DataWithPagination<EntityItem>> => {
  let path = `/v1/notifications/entity-types/${entityType}/items/search`;
  if (search) path += `?search=${encodeURIComponent(search)}`;
  return paginatedApiCall<EntityItem>(path, pagination, transformEntityItem);
};
